import { useContext, type ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { BandStandingContext, useRegisterBandStanding } from './bandStanding';
import { EyebrowLabel } from './EyebrowLabel';
import { PageSectionContext } from './pageSection';

export interface PageHeaderProps {
  /** What kind of page this is — "Workspace", "Board". Omitted where the title already says it. */
  eyebrow?: string;
  title: ReactNode;
  /**
   * A control beside the title, outside the heading so it is not read as part
   * of the page's name: Settings' rename pencil (spec 194).
   */
  titleAction?: ReactNode;
  /** One line under the title. Prose on a landing page, status on a working one. */
  description?: ReactNode;
  /** Sits between the title block and the actions: the board's state filters, a count, a badge. */
  aside?: ReactNode;
  /** The page's controls. Exactly one of them should be the filled primary. */
  actions?: ReactNode;
  /**
   * Ties this page's title to the element that was clicked to reach it, so the
   * two are the same object across the navigation (spec 075 FR-026).
   *
   * Only one element may carry a given name in a single frame, so pass this
   * from a page the reader arrives at, never from something that stays on
   * screen. The shell's sidebar lists workflows and never unmounts: naming a
   * row there would collide with the page it opened and the browser would drop
   * the transition entirely.
   */
  titleViewTransitionName?: string;
  /**
   * `hero` is for a page that is the front door rather than a room inside the
   * app — no sidebar, centred column, nothing above it in the breadcrumb.
   *
   * `compact` is one short line for a page whose content is the point, the
   * Overview (spec 153 FR-016): a smaller title, no eyebrow, and a band at
   * most 72px tall. The breadcrumb already says which workspace this is.
   */
  size?: 'default' | 'hero' | 'compact';
  /**
   * `inverse` draws the header as a band of Capgemini Dark Blue bleeding to
   * the page's edges — the one strong brand moment a working page gets
   * (spec 122 FR-010). The main pages of a workspace carry it, its Settings
   * among them since spec 194; the personal settings pages do not.
   */
  surface?: 'page' | 'inverse';
  /**
   * Content that belongs inside the band under the title block — the
   * Library's search field. Only rendered on the `inverse` surface.
   */
  children?: ReactNode;
  className?: string;
}

/**
 * Every page's header, in one place.
 *
 * There were four copies of this markup before it existed — `PagePlaceholder`
 * had one, `BoardsPage` had a hand-copied duplicate, and the board and the
 * builder had each grown their own. They drifted exactly as you would expect:
 * different title weights, an eyebrow on some pages and not others, and no
 * shared answer for where the page's actions go. Pages describe their header
 * now instead of drawing one.
 *
 * A div rather than a `header`: a second banner landmark on every page would
 * make the shell's real banner ambiguous to assistive technology.
 *
 * The band carries the `dark` class. It is the dark theme's surface shown in
 * the light theme, so rather than restyle every control that can sit in it —
 * buttons, the capacity meter, the Live pill, a search field — the band scopes
 * the dark theme's token block, whose pairings are already measured. In the
 * dark theme proper it is `--cg-blue-dark` on the deeper navy page, with a
 * bottom hairline so the opening of the page does not read as a lighter slab.
 */
export function PageHeader({
  eyebrow,
  title,
  titleAction,
  description,
  aside,
  actions,
  titleViewTransitionName,
  size = 'default',
  surface = 'page',
  children,
  className,
}: PageHeaderProps) {
  const hero = size === 'hero';
  const compact = size === 'compact';
  // One tab of a grouped entry (spec 224): the header names the section, the strip
  // under it names the page. The section also decides the surface (spec 227): its
  // pages open with the band whichever tab is open, in their loading and refusal
  // states too.
  const section = useContext(PageSectionContext);
  const inverse = (section?.surface ?? surface) === 'inverse';
  // Where the reader stands, on every band of a workspace they are not a member
  // of (spec 196 US1); only the band carries it, the top bar has its own chip.
  const bandStanding = useContext(BandStandingContext);
  const standing = inverse ? bandStanding : null;
  // While this band says it, the top bar does not (one badge per page).
  useRegisterBandStanding(standing !== null);
  const shownTitle = section ? section.title : title;
  const shownDescription = section?.description ?? description;
  // In a toolbar section the page's controls move under the tabs (spec 224).
  const inToolbar = section?.toolbar === true;
  const headerAside = inToolbar ? undefined : aside;
  const headerActions = inToolbar ? undefined : actions;
  // Light at 3xl, per FD-14, on the two surfaces that are allowed that size:
  // the front door and the band. Everywhere else 2xl Medium, the one title
  // size every room inside the app uses (spec 122 FR-007); it was xl, which
  // sat too close to a section heading to read as the page's name.
  const large = hero || (inverse && !compact);

  const heading = (
    <h1
      style={
        titleViewTransitionName
          ? { viewTransitionName: titleViewTransitionName }
          : undefined
      }
      // A workflow's name is the band's title, and a name can be long.
      // Three lines, then an ellipsis with the whole name on hover — the
      // band grows for a long name rather than clipping it, but not
      // without limit. Only a name is clamped: line-clamp sets overflow:
      // hidden, which would cut off the focus ring of a control in the
      // title's place (the rename field).
      title={inverse && typeof shownTitle === 'string' ? shownTitle : undefined}
      className={cn(
        'tracking-tight',
        inverse ? 'text-on-dark' : 'text-foreground',
        inverse && typeof shownTitle === 'string' && 'line-clamp-3',
        // A phone's band holds a title, not a headline (spec 260).
        large
          ? 'text-3xl font-light max-md:text-2xl short-touch:text-2xl'
          : compact
            ? 'text-xl font-light'
            : 'text-2xl font-medium',
      )}
    >
      {shownTitle}
    </h1>
  );

  const header = (
    <div
      className={cn(
        // Centred, not top-aligned: the title block is one, two or three lines
        // deep depending on the page, and anything sitting beside it should
        // read as level with the title rather than pinned to the eyebrow above
        // it.
        'flex flex-wrap items-center justify-between gap-x-6 gap-y-3 max-md:gap-y-2',
        !inverse && className,
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        {/* `EyebrowLabel`, not a second copy of its classes. This was written
            out here at `text-xs` while that component sets `text-xs`, so
            the board showed two sizes of the same all-caps label at once. */}
        {eyebrow && !compact ? (
          // Light Blue on the band: a 7.46:1 pairing, measured, under the
          // WD-04 waiver (docs/brand.md), which the band extends to the
          // inverse surface in both themes.
          // Not on a phone (spec 260): the breadcrumb above already says it.
          <EyebrowLabel
            className={cn(
              inverse && 'text-accent-line',
              'max-md:hidden short-touch:hidden',
            )}
          >
            {eyebrow}
          </EyebrowLabel>
        ) : null}
        {titleAction ? (
          <div className="flex min-w-0 items-center gap-2">
            {heading}
            {titleAction}
          </div>
        ) : (
          heading
        )}
        {shownDescription ? (
          <p
            className={cn(
              'max-w-prose text-sm leading-relaxed',
              inverse
                ? // The description is the first thing to go on a narrow
                  // screen: the band must stay a band, not a page.
                  'text-on-dark/80 max-md:hidden short-touch:hidden'
                : 'text-muted-foreground',
            )}
          >
            {shownDescription}
          </p>
        ) : null}
      </div>

      {headerAside ? (
        // Wraps on a phone (spec 265): a third pill ran off the screen's edge.
        <div className="flex items-center gap-2 max-md:min-w-0 max-md:flex-wrap">
          {headerAside}
        </div>
      ) : null}

      {headerActions || standing ? (
        <div
          className={cn(
            'ms-auto flex shrink-0 items-center gap-2',
            // Under the title on a narrow screen rather than squeezed beside
            // it; `basis-full` is what moves them to their own row.
            inverse && 'max-md:ms-0 max-md:basis-full',
            // A page's main action marked `data-phone-row` gets the same row
            // on a light header, so it can fill the width (spec 273).
            'max-md:has-[>[data-phone-row]]:ms-0 max-md:has-[>[data-phone-row]]:basis-full',
          )}
        >
          {standing}
          {headerActions}
        </div>
      ) : null}
    </div>
  );

  const drawn = !inverse ? (
    header
  ) : (
    <div
      data-surface="inverse"
      className={cn(
        // `-mx-6 -mt-6` undo the page's `p-6` so the band reaches the edges;
        // `py-5` is what holds a one-line title with a description at 120px.
        // A phone's page gutter is 16px, so its band undoes 16 (spec 260),
        // and the band is a title bar there rather than a masthead.
        'dark bg-surface-inverse text-on-dark dark:border-border -mx-6 -mt-6 flex flex-col gap-4 px-6 sm:px-10 dark:border-b',
        'max-md:-mx-4 max-md:-mt-4 max-md:gap-3 max-md:px-4 max-md:py-3',
        // A phone on its side: the band would take half the screen (spec 265).
        'short-touch:gap-2 short-touch:py-2!',
        compact ? 'py-3' : 'py-5',
        className,
      )}
    >
      {header}
      {children}
    </div>
  );

  if (!section) return drawn;
  const toolbar =
    inToolbar && (actions || aside) ? (
      // On a phone each control takes the full width, one under another
      // (spec 277), rather than wrapping to wherever it lands.
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 max-md:flex-col max-md:items-stretch max-md:gap-y-2">
        {actions}
        {aside ? (
          <div className="ms-auto flex items-center gap-2 max-md:ms-0 max-md:[&>*]:flex-1">
            {aside}
          </div>
        ) : null}
      </div>
    ) : null;
  if (!inverse) {
    return (
      <>
        {drawn}
        {section.tabs}
        {toolbar}
      </>
    );
  }
  // Under the band the strip sits flush, as Settings' always has (spec 227): one
  // block with its own, tighter gap, so the page's `gap-6` is not doubled between
  // the band and the strip.
  return (
    <div className="flex flex-col gap-5">
      {drawn}
      {section.tabs}
      {toolbar}
    </div>
  );
}
